import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BorderBox, Spinner, GradientLine, Pill } from "../components/index.js";
import {
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "../assets/icons/index.js";
import requestHandler from "../utils/requestHandler.js";
import { noteToPlainText, noteToSafeHtml } from "../utils/noteHtml.js";

const journalFilters = [
  { name: "all", label: "All Entries" },
  { name: "highlights", label: "Highlights and Highlight Notes" },
  { name: "lessonNotes", label: "Lesson Notes" },
];

const getHighlightColor = (color) => {
  switch (color) {
    case "yellow":
      return "bg-lz-yellow";
    case "green":
      return "bg-lz-light-green";
    case "pink":
      return "bg-lz-light-pink";
    default:
      return "bg-gray-200";
  }
};

export default function Journal() {
  const navigate = useNavigate();
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");

  useEffect(() => {
    const loadAnnotations = async () => {
      setLoading(true);
      const res = await requestHandler({
        path: "annotations",
        method: "GET",
      });

      if (res.success) {
        const grouped = {};
        for (const item of res.data) {
          const key = item.lesson_slug;
          if (!grouped[key]) {
            grouped[key] = {
              lesson_slug: item.lesson_slug,
              lesson_title: item.lesson_title,
              degree_name: item.degree_name,
              activeAnnotations: {},
              archivedBlobs: [],
              finalNote: null,
            };
          }
          if (item.is_note) {
            if (item.note) {
              grouped[key].finalNote = {
                note: item.note,
                timestamp: item.updated_at,
              };
            }
          } else {
            const parsed = JSON.parse(item.annotation || '{}');
            if (item.archived) {
              grouped[key].archivedBlobs.push(parsed);
            } else {
              grouped[key].activeAnnotations = parsed;
            }
          }
        }
        setAnnotations(Object.values(grouped));
      } else {
        console.error("Error loading annotations:", res.error);
      }
      setLoading(false);
    };

    loadAnnotations();
  }, []);

  const handleDeleteNote = async (
    lessonSlug,
    annotationId,
    currentAnnotations,
    archivedBlobs,
  ) => {
    const updatedAnnotations = { ...currentAnnotations };
    if (updatedAnnotations[annotationId]) {
      updatedAnnotations[annotationId].note = "";
    }

    const res = await requestHandler({
      path: "annotate",
      method: "POST",
      body: {
        slug: lessonSlug,
        annotations: JSON.stringify(updatedAnnotations),
        archives: archivedBlobs.map((blob) => JSON.stringify(blob)),
      },
    });

    if (res.success) {
      setAnnotations((prev) =>
        prev.map((item) =>
          item.lesson_slug === lessonSlug
            ? { ...item, activeAnnotations: updatedAnnotations }
            : item,
        ),
      );
    }
  };

  const handleDeleteArchivedAnnotation = async (
    lessonSlug,
    blobIndex,
    annotationId,
    currentAnnotations,
    archivedBlobs,
  ) => {
    const updatedBlobs = archivedBlobs
      .map((blob, i) => {
        if (i !== blobIndex) return blob;
        const newBlob = { ...blob };
        delete newBlob[annotationId];
        return newBlob;
      })
      .filter((blob) => Object.keys(blob).length > 0);

    const res = await requestHandler({
      path: "annotate",
      method: "POST",
      body: {
        slug: lessonSlug,
        annotations: JSON.stringify(currentAnnotations),
        archives: updatedBlobs.map((blob) => JSON.stringify(blob)),
      },
    });

    if (res.success) {
      setAnnotations((prev) =>
        prev
          .map((item) =>
            item.lesson_slug === lessonSlug
              ? { ...item, archivedBlobs: updatedBlobs }
              : item,
          )
          .filter(
            (item) =>
              Object.keys(item.activeAnnotations).length > 0 ||
              item.archivedBlobs.length > 0,
          ),
      );
    }
  };

  const handleHighlightClick = (lessonSlug, highlightId) => {
    navigate(`/lessons/${lessonSlug}`, { state: { highlightId } });
  };

  const handleFinalNoteClick = (lessonSlug) => {
    navigate(`/lessons/${lessonSlug}`, { state: { scrollToNote: true } });
  };

  const handleSearch = () => {
    setActiveSearch(searchQuery.trim());
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearch("");
  };

  const matchesQuery = (text) => {
    if (!activeSearch) return true;
    return text && text.toLowerCase().includes(activeSearch.toLowerCase());
  };

  const filterAnnotations = (items) => {
    if (!activeSearch) return items;
    const query = activeSearch.toLowerCase();

    return items.filter((item) => {
      const lessonMatches =
        item.lesson_title.toLowerCase().includes(query) ||
        item.degree_name.toLowerCase().includes(query);

      const activeMatch = Object.values(item.activeAnnotations).some(
        (v) =>
          (v.text && v.text.toLowerCase().includes(query)) ||
          (v.note && v.note.toLowerCase().includes(query)),
      );

      const archiveMatch = item.archivedBlobs.some((blob) =>
        Object.values(blob).some(
          (v) =>
            (v.text && v.text.toLowerCase().includes(query)) ||
            (v.note && v.note.toLowerCase().includes(query)),
        ),
      );

      const finalNoteMatch =
        item.finalNote &&
        noteToPlainText(item.finalNote.note).toLowerCase().includes(query);

      return lessonMatches || activeMatch || archiveMatch || finalNoteMatch;
    });
  };

  const applyFilterMode = (items) => {
    return items.reduce((acc, item) => {
      let { finalNote, activeAnnotations, archivedBlobs } = item;

      if (filterMode === "highlights") {
        finalNote = null;
      } else if (filterMode === "lessonNotes") {
        activeAnnotations = {};
        archivedBlobs = [];
      }

      const hasContent =
        finalNote ||
        Object.keys(activeAnnotations).length > 0 ||
        archivedBlobs.length > 0;

      if (hasContent) {
        acc.push({ ...item, finalNote, activeAnnotations, archivedBlobs });
      }
      return acc;
    }, []);
  };

  const filteredAnnotations = filterAnnotations(applyFilterMode(annotations));

  return (
    <div id="journal-content" className="flex flex-col justify-between h-[calc(100vh-4rem)] pb-4">
      <BorderBox padding="p-8" overflow>
        <h1 className="text-lz-ink-mid dark:text-lz-concrete text-5xl mb-6">
          Journal
        </h1>
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="relative flex-grow">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 fill-lz-ink-mid dark:fill-lz-concrete" />
            <input
              type="text"
              aria-label="Search journal"
              placeholder="Search journal..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="w-full pl-10 pr-4 py-3 rounded-2xl border border-lz-paper-border dark:border-lz-charcoal-border bg-lz-concrete dark:bg-lz-ink text-black dark:text-lz-concrete placeholder-lz-ink-mid dark:placeholder-lz-concrete focus:outline-none focus:ring-2 focus:ring-lz-candle-dim transition-all duration-300"
            />
          </div>
          {activeSearch ? (
            <button
              onClick={handleClearSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-lz-ember dark:bg-lz-ink-mid text-white dark:text-lz-candle-dim border border-lz-ember dark:border-lz-candle-dim hover:bg-lz-ink-mid hover:border-lz-ink-mid dark:hover:bg-lz-candle-dim dark:hover:border-lz-candle-dim dark:hover:text-lz-ink-mid font-semibold transition-all duration-200"
            >
              <XMarkIcon className="w-3 h-3 fill-current" />
              Clear
            </button>
          ) : (
            <button
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-lz-ember dark:bg-lz-ink-mid text-white dark:text-lz-candle-dim border border-lz-ember dark:border-lz-candle-dim hover:bg-lz-ink-mid hover:border-lz-ink-mid dark:hover:bg-lz-candle-dim dark:hover:border-lz-candle-dim dark:hover:text-lz-ink-mid font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Search
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mb-6">
          {journalFilters.map((filter) => (
            <Pill
              key={filter.name}
              selected={filterMode === filter.name}
              onClick={() => setFilterMode(filter.name)}
            >
              {filter.label}
            </Pill>
          ))}
        </div>
        {loading ? (
          <Spinner />
        ) : filteredAnnotations.length === 0 && activeSearch ? (
          <p className="text-center text-lz-ink-mid dark:text-lz-concrete py-8">
            No results found for &ldquo;{activeSearch}&rdquo;
          </p>
        ) : (
          filteredAnnotations.map((item, index) => {
            const lessonMatches = activeSearch &&
              (item.lesson_title.toLowerCase().includes(activeSearch.toLowerCase()) ||
               item.degree_name.toLowerCase().includes(activeSearch.toLowerCase()));

            const sortedAnnotations = Object.entries(item.activeAnnotations)
              .map(([key, value]) => ({ id: key, ...value }))
              .filter((a) =>
                !activeSearch || lessonMatches ||
                matchesQuery(a.text) || matchesQuery(a.note),
              );

            const visibleArchived = item.archivedBlobs
              .flatMap((blob, blobIndex) =>
                Object.entries(blob).map(([key, value]) => ({
                  id: key,
                  blobIndex,
                  ...value,
                })),
              )
              .filter((entry) =>
                !activeSearch || lessonMatches ||
                matchesQuery(entry.text) || matchesQuery(entry.note),
              );

            return (
              <div key={item.lesson_slug} className="mb-8">
                {index > 0 && <GradientLine className="mb-6" />}
                <h2 className="text-lz-ink-mid dark:text-lz-concrete text-2xl mb-4">
                  {item.lesson_title} - {item.degree_name}
                </h2>
                {item.finalNote && matchesQuery(noteToPlainText(item.finalNote.note)) && (
                  <>
                    <h3 className="text-lz-ink-mid dark:text-lz-concrete text-lg mb-2">
                      Lesson Notes
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(item.finalNote.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div
                      onClick={() => handleFinalNoteClick(item.lesson_slug)}
                      className="bg-lz-concrete dark:bg-lz-ink p-3 rounded text-md text-lz-ink dark:text-lz-concrete break-words mb-4 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0 cursor-pointer transition-shadow duration-200 hover:shadow-[0_0_8px_2px_rgba(255,255,255,0.5)]"
                      dangerouslySetInnerHTML={{
                        __html: noteToSafeHtml(item.finalNote.note),
                      }}
                    />
                  </>
                )}
                {sortedAnnotations.length > 0 && (
                  <>
                    <h3 className="text-lz-ink-mid dark:text-lz-concrete text-lg mb-2">
                      Highlights and Highlight Notes
                    </h3>
                    <div className="space-y-4">
                      {sortedAnnotations.map((annotation) => (
                        <div key={annotation.id} className="text-white">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(annotation.timestamp).toLocaleString()}
                            </span>
                          </div>
                          {(lessonMatches || matchesQuery(annotation.text)) && (
                            <div
                              onClick={() =>
                                handleHighlightClick(
                                  item.lesson_slug,
                                  annotation.id,
                                )
                              }
                              className={`text-black ${getHighlightColor(
                                annotation.color,
                              )} p-2 rounded mb-2 text-md cursor-pointer transition-shadow duration-200 hover:shadow-[0_0_8px_2px_rgba(255,255,255,0.5)]`}
                            >
                              {annotation.text}
                            </div>
                          )}
                          {annotation.note && matchesQuery(annotation.note) && (
                            <div className="flex items-center gap-2 pl-6">
                              <p className="text-sm text-lz-ink dark:text-lz-concrete flex-grow break-words max-w-[calc(100%-2rem)]">
                                {annotation.note}
                              </p>
                              <button
                                onClick={() =>
                                  handleDeleteNote(
                                    item.lesson_slug,
                                    annotation.id,
                                    item.activeAnnotations,
                                    item.archivedBlobs,
                                  )
                                }
                                title="Delete note"
                              >
                                <TrashIcon className="m-auto h-4 w-4 fill-lz-ink-mid dark:fill-lz-concrete hover:fill-red-400" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {visibleArchived.length > 0 && (
                  <>
                    <h3 className="text-lz-ink-mid dark:text-lz-concrete text-lg mt-6 mb-2">
                      Archive
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-300 mb-3">
                      These notes have been unlinked from the associated lesson
                      because changes have been made to the lesson.
                    </p>
                    <div className="space-y-3">
                      {visibleArchived.map((entry) => (
                          <div
                            key={entry.id}
                            className="text-white mb-3"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {new Date(
                                  entry.timestamp,
                                ).toLocaleString()}
                              </span>
                            </div>
                            {(lessonMatches || matchesQuery(entry.text)) && (
                              <div className="flex items-center gap-2 mb-2">
                                <div className="text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 p-2 rounded text-md flex-grow">
                                  {entry.text}
                                </div>
                                <button
                                  onClick={() =>
                                    handleDeleteArchivedAnnotation(
                                      item.lesson_slug,
                                      entry.blobIndex,
                                      entry.id,
                                      item.activeAnnotations,
                                      item.archivedBlobs,
                                    )
                                  }
                                  title="Delete archived highlight"
                                  className="shrink-0"
                                >
                                  <TrashIcon className="h-4 w-4 fill-lz-ink-mid dark:fill-lz-concrete hover:fill-red-400" />
                                </button>
                              </div>
                            )}
                            {entry.note && matchesQuery(entry.note) && (
                              <div className="pl-6 mb-2">
                                <p className="text-sm text-lz-ink dark:text-lz-concrete break-words">
                                  {entry.note}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </BorderBox>
    </div>
  );
}
